/**
 * EditHabitDrawer
 *
 * A bottom drawer for editing habit settings.
 * Replaces the standalone EditHabitPage — accessible from any page.
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Spinner } from '@/components/ui/spinner'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer'
import { CompactEmojiPicker } from '@/components/habits/EmojiPickerSection'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { format } from 'date-fns'
import type { Habit, UserProfile } from '@/types/database'
import { hapticSelection, hapticLight, hapticMedium, hapticSuccess } from '@/lib/native/haptics'
import { habitsKeys, profileKeys } from '@/lib/query-keys'
import { useSubscription } from '@/lib/hooks/use-subscription'
import { PaywallDrawer } from '@/components/subscription/PaywallDrawer'
import { scheduleHabitReminders } from '@/lib/native/habit-notifications'
import { ReminderTimePicker } from '@/components/ui/reminder-time-picker'

const EDIT_COST = 5

interface EditHabitDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  habitId: string | null
}

export function EditHabitDrawer({ open, onOpenChange, habitId }: EditHabitDrawerProps) {
  const queryClient = useQueryClient()

  const [habit, setHabit] = useState<Habit | null>(null)
  const [shardBalance, setShardBalance] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  // Form state
  const [title, setTitle] = useState('')
  const [emoji, setEmoji] = useState<string | null>(null)
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
  const [identityLabel, setIdentityLabel] = useState('')
  const [goldCriteria, setGoldCriteria] = useState('')
  const [silverCriteria, setSilverCriteria] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [requiresProof, setRequiresProof] = useState(true)
  const [showNotesPrompt, setShowNotesPrompt] = useState(true)
  const [cardColor, setCardColor] = useState<string | null>(null)

  // Frequency state
  const [frequencyType, setFrequencyType] = useState<'daily' | 'weekly' | 'monthly' | 'yearly' | 'specific_days'>('daily')
  const [timesPerPeriod, setTimesPerPeriod] = useState(1)
  const [specificDays, setSpecificDays] = useState<number[]>([])

  // Conquest state
  const [isCompletable, setIsCompletable] = useState(false)
  const [targetType, setTargetType] = useState<'manual' | 'streak' | 'date' | null>(null)
  const [targetStreak, setTargetStreak] = useState(30)
  const [targetDate, setTargetDate] = useState('')

  // Reminders state
  const [reminderEnabled, setReminderEnabled] = useState(false)
  const [reminderTime, setReminderTime] = useState('09:00')

  // Custom date picker state
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)
  
  const [showPaywall, setShowPaywall] = useState(false)
  const { isPremium } = useSubscription()

  const hasInsufficientShards = shardBalance < EDIT_COST

  // Fetch habit and profile data when drawer opens
  useEffect(() => {
    if (!open || !habitId) {
      setLoading(true)
      setHabit(null)
      setShowSuccess(false)
      return
    }

    const fetchData = async () => {
      setLoading(true)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: habitData, error: habitError } = await supabase
        .from('habits')
        .select('*')
        .eq('id', habitId)
        .eq('user_id', user.id)
        .single()

      if (habitError || !habitData) {
        toast.error('Habit not found')
        onOpenChange(false)
        return
      }

      const h = habitData as Habit
      setHabit(h)

      // Initialize form state from habit
      setTitle(h.title)
      setEmoji(h.emoji)
      setIdentityLabel(h.identity_label || '')
      setGoldCriteria(h.gold_criteria)
      setSilverCriteria(h.silver_criteria || '')
      setIsPrivate(h.is_private)
      setRequiresProof(h.requires_proof !== false)
      setShowNotesPrompt(h.show_notes_prompt !== false)
      setCardColor(h.card_color || null)
      setFrequencyType(h.frequency_type)
      setTimesPerPeriod(h.times_per_period)
      setSpecificDays(h.schedule_days || [])
      setIsCompletable(h.is_completable)
      setTargetType(h.target_type)
      setTargetStreak(h.target_streak || 30)
      setTargetDate(h.target_date ? h.target_date.split('T')[0] : '')
      setReminderEnabled(h.reminder_enabled || false)
      setReminderTime(h.reminder_time || '09:00')

      // Fetch profile for shard balance
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('currency_shards')
        .eq('id', user.id)
        .single()

      if (profileData) {
        setShardBalance((profileData as UserProfile).currency_shards)
      }

      setLoading(false)
    }

    fetchData()
  }, [open, habitId, onOpenChange])

  // Calculate date from months ahead
  const setDatePreset = (months: number) => {
    const today = new Date()
    const d = new Date(today)
    d.setMonth(d.getMonth() + months)
    setTargetDate(d.toISOString().split('T')[0])
  }

  // Handle custom date selection from calendar
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setTargetDate(date.toISOString().split('T')[0])
      setShowCustomDatePicker(false)
    }
  }

  const handleSave = async () => {
    if (!habit) return

    if (!title.trim()) {
      toast.error('Title is required')
      return
    }

    if (requiresProof && !goldCriteria.trim()) {
      toast.error('Gold criteria is required for proof habits')
      return
    }

    if (hasInsufficientShards) {
      toast.error('Insufficient shards', {
        description: `You need ${EDIT_COST} shards to edit a habit`,
      })
      return
    }

    setIsSaving(true)

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      toast.error('Not authenticated')
      setIsSaving(false)
      return
    }

    // Deduct shards first
    const { error: shardError } = await supabase
      .from('user_profiles')
      .update({ currency_shards: shardBalance - EDIT_COST })
      .eq('id', user.id)

    if (shardError) {
      toast.error('Failed to deduct shards')
      setIsSaving(false)
      return
    }

    // Update habit
    const { data: updateData, error: updateError } = await supabase
      .from('habits')
      .update({
        title: title.trim(),
        emoji: emoji,
        identity_label: identityLabel.trim() || null,
        gold_criteria: requiresProof ? goldCriteria.trim() : 'Quick completion',
        silver_criteria: requiresProof ? (silverCriteria.trim() || null) : null,
        is_private: isPrivate,
        requires_proof: requiresProof,
        show_notes_prompt: showNotesPrompt,
        card_color: cardColor || null,
        frequency_type: frequencyType,
        times_per_period: timesPerPeriod,
        schedule_days: frequencyType === 'specific_days' ? specificDays : null,
        is_completable: isCompletable,
        target_type: isCompletable ? targetType : null,
        target_streak: isCompletable && targetType === 'streak' ? targetStreak : null,
        target_date: isCompletable && targetType === 'date' && targetDate ? targetDate : null,
        reminder_enabled: reminderEnabled,
        reminder_time: reminderEnabled ? reminderTime : null,
      })
      .eq('id', habit.id)
      .eq('user_id', user.id)
      .select()
      .single()

    setIsSaving(false)

    if (updateError) {
      // Refund shards if update failed
      await supabase
        .from('user_profiles')
        .update({ currency_shards: shardBalance })
        .eq('id', user.id)

      toast.error('Failed to update habit', { description: updateError.message })
      return
    }
    
    // Reschedule notifications
    if (updateData) {
      scheduleHabitReminders(updateData as any)
    }

    // Invalidate caches and refresh profile immediately so TopBar updates
    window.dispatchEvent(new CustomEvent('miac:profile-refresh'))
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: habitsKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: habitsKeys.detail(habit.id) }),
      queryClient.invalidateQueries({ queryKey: profileKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['wallet'] }),
    ])

    // Show success state with haptic
    hapticSuccess()
    setShowSuccess(true)

    // Close after the success animation plays
    setTimeout(() => {
      onOpenChange(false)
      toast.success('Habit updated!', { description: `−${EDIT_COST} shards` })
    }, 1200)
  }

  const isChangingType = habit && habit.requires_proof !== requiresProof
  const isChangingCore = habit && (title !== habit.title || goldCriteria !== habit.gold_criteria)
  const isLinkedToChallenge = false // TODO: Check if habit is linked to a challenge

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="h-[90vh] max-h-[90vh] flex flex-col"
      >
        <DrawerTitle className="sr-only">Edit Habit</DrawerTitle>

        {/* Success Overlay */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-t-[10px]"
            >
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
              >
                <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mb-4">
                  <motion.svg
                    viewBox="0 0 24 24"
                    className="w-10 h-10 text-emerald-500"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                  >
                    <motion.path
                      d="M5 13l4 4L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                    />
                  </motion.svg>
                </div>
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg font-semibold text-slate-900 dark:text-white"
              >
                Habit Updated!
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                className="text-sm text-slate-500 dark:text-slate-400 mt-1"
              >
                −{EDIT_COST} 💎
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="shrink-0 px-4 sm:px-6 pt-3 pb-2 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-950 z-10 sticky top-0 rounded-t-[10px]">
          <div className="flex items-center gap-2.5">
            <FluentEmoji emoji="📝" size={22} />
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">Edit Habit</h2>
              <p className="text-xs text-slate-500">
                Cost: <span className="font-medium text-amber-600 dark:text-amber-400">{EDIT_COST}</span>
                {' 💎 • '}
                Balance: <span className={`font-medium ${hasInsufficientShards ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {shardBalance}
                </span>
              </p>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={loading || isSaving || !title.trim() || (requiresProof && !goldCriteria.trim()) || hasInsufficientShards}
            size="sm"
            className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl px-4"
          >
            {isSaving ? <Spinner size="sm" /> : 'Save'}
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : !habit ? null : (
            <div className="px-4 sm:px-6 py-4 space-y-5">
              {/* Insufficient shards warning */}
              {hasInsufficientShards && (
                <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 flex gap-3">
                  <FluentEmoji emoji="⚠️" size={20} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-200 text-sm">Insufficient Shards</p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                      You need {EDIT_COST} shards. You have {shardBalance}. Complete habits to earn more!
                    </p>
                  </div>
                </div>
              )}

              {/* Title & Emoji */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Title & Emoji</label>
                <div className="flex gap-2">
                  <CompactEmojiPicker
                    emoji={emoji}
                    onSelect={setEmoji}
                    open={emojiPickerOpen}
                    onOpenChange={setEmojiPickerOpen}
                    allowRemove
                  />
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Morning run"
                    className="text-base flex-1"
                  />
                </div>
              </div>

              {/* Identity Label */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Identity <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <Input
                  value={identityLabel}
                  onChange={(e) => setIdentityLabel(e.target.value)}
                  placeholder="e.g., I am a runner"
                />
              </div>

              {/* Frequency */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                  <FluentEmoji emoji="📅" size={14} /> Frequency
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { hapticLight(); setFrequencyType('daily'); setTimesPerPeriod(1); setSpecificDays([]) }}
                    className={`p-2.5 rounded-xl border text-sm font-medium transition-all ${
                      frequencyType === 'daily'
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    Every day
                  </button>
                  <button
                    type="button"
                    onClick={() => { hapticLight(); setFrequencyType('weekly'); setTimesPerPeriod(3); setSpecificDays([]) }}
                    className={`p-2.5 rounded-xl border text-sm font-medium transition-all ${
                      frequencyType === 'weekly'
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    Weekly
                  </button>
                </div>

                {(frequencyType === 'weekly' || frequencyType === 'monthly') && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max={frequencyType === 'weekly' ? 7 : 31}
                      value={timesPerPeriod}
                      onChange={(e) => setTimesPerPeriod(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20"
                    />
                    <span className="text-sm text-slate-500">times per {frequencyType === 'weekly' ? 'week' : 'month'}</span>
                  </div>
                )}

                <div>
                  <button
                    type="button"
                    onClick={() => {
                      hapticLight()
                      if (frequencyType === 'specific_days') { setFrequencyType('daily'); setSpecificDays([]) }
                      else { setFrequencyType('specific_days'); setSpecificDays([1, 3, 5]) }
                    }}
                    className="text-xs text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    {frequencyType === 'specific_days' ? 'Cancel specific days' : 'Or pick specific days'}
                  </button>

                  {frequencyType === 'specific_days' && (
                    <div className="flex gap-1 mt-2">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            hapticSelection()
                            setSpecificDays(prev =>
                              prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx].sort()
                            )
                          }}
                          className={`w-9 h-9 rounded-full text-sm font-medium transition-all ${
                            specificDays.includes(idx)
                              ? 'bg-violet-500 text-white'
                              : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Habit Type Toggle */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Habit Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { hapticMedium(); setRequiresProof(true) }}
                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                      requiresProof
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                        : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <FluentEmoji emoji="📸" size={24} />
                    <p className={`text-sm font-medium ${requiresProof ? 'text-violet-700 dark:text-violet-300' : 'text-slate-600 dark:text-slate-400'}`}>Photo Proof</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { hapticMedium(); setRequiresProof(false) }}
                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                      !requiresProof
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                        : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <FluentEmoji emoji="✅" size={24} />
                    <p className={`text-sm font-medium ${!requiresProof ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-400'}`}>Quick Done</p>
                  </button>
                </div>
                {isChangingType && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <FluentEmoji emoji="⚠️" size={12} /> Changing habit type
                  </p>
                )}
              </div>

              {/* Gold / Silver Criteria (proof only) */}
              <AnimatePresence mode="wait">
                {requiresProof ? (
                  <motion.div
                    key="proof-fields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">🥇 Gold Standard</label>
                      <Textarea
                        value={goldCriteria}
                        onChange={(e) => setGoldCriteria(e.target.value)}
                        placeholder="What does perfect execution look like?"
                        rows={2}
                      />
                      <p className="text-xs text-slate-500">+10 💎 • AI uses this to verify your photos</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        🥈 Silver Standard <span className="text-slate-400 font-normal">(optional)</span>
                      </label>
                      <Textarea
                        value={silverCriteria}
                        onChange={(e) => setSilverCriteria(e.target.value)}
                        placeholder="What counts as showing up?"
                        rows={2}
                      />
                      <p className="text-xs text-slate-500">+5 💎</p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="quick-info"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50"
                  >
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      <FluentEmoji emoji="✅" size={16} className="inline mr-2" />
                      Quick habits don't require photo proof. Just tap "Done" to complete!
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Card Color */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Card Color</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'amber', cls: 'bg-amber-400' },
                    { key: 'blue', cls: 'bg-blue-400' },
                    { key: 'violet', cls: 'bg-violet-400' },
                    { key: 'rose', cls: 'bg-rose-400' },
                    { key: 'teal', cls: 'bg-teal-400' },
                    { key: 'orange', cls: 'bg-orange-400' },
                    { key: 'emerald', cls: 'bg-emerald-400' },
                    { key: 'indigo', cls: 'bg-indigo-400' },
                  ].map((color) => (
                    <button
                      key={color.key}
                      type="button"
                      onClick={() => { hapticLight(); setCardColor(cardColor === color.key ? null : color.key) }}
                      className={`w-8 h-8 rounded-full ${color.cls} transition-all ${
                        cardColor === color.key
                          ? 'ring-2 ring-offset-2 ring-slate-900 dark:ring-white dark:ring-offset-slate-900 scale-110'
                          : 'opacity-80 hover:opacity-100'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Toggle Section */}
              <div className="space-y-2">
                {/* Reminder Setting (Pro Feature) */}
                <div className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                  reminderEnabled
                    ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800'
                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="relative">
                        <FluentEmoji emoji="🔔" size={18} />
                        {!isPremium && (
                          <div className="absolute -bottom-1 -right-1 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-full w-3.5 h-3.5 flex items-center justify-center text-[8px] shadow-sm">
                            🔒
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <p className={`font-medium text-sm ${reminderEnabled ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-900 dark:text-white'}`}>
                          Reminders {isPremium ? '' : '(Pro)'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {reminderEnabled ? 'We will remind you' : 'Get a notification'}
                        </p>
                      </div>
                    </div>
                    <div 
                      className={`relative w-10 h-6 rounded-full transition-colors shrink-0 cursor-pointer ${reminderEnabled ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                      onClick={(e) => {
                        hapticMedium()
                        if (!isPremium) {
                          e.preventDefault()
                          setShowPaywall(true)
                          return
                        }
                        setReminderEnabled(!reminderEnabled)
                      }}
                    >
                      <motion.div
                        animate={{ x: reminderEnabled ? 18 : 2 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                      />
                    </div>
                  </div>

                  {reminderEnabled && (
                    <div className="flex items-center justify-between pt-3 border-t border-indigo-100 dark:border-indigo-800/50 mt-3">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Reminder Time
                      </label>
                      <ReminderTimePicker
                        value={reminderTime}
                        onChange={setReminderTime}
                      />
                    </div>
                  )}
                </div>

                {/* Privacy */}
                <button
                  type="button"
                  onClick={() => { hapticMedium(); setIsPrivate(!isPrivate) }}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <FluentEmoji emoji={isPrivate ? '🔒' : '🔓'} size={18} />
                    <div className="text-left">
                      <p className="font-medium text-slate-900 dark:text-white text-sm">{isPrivate ? 'Private' : 'Public'}</p>
                      <p className="text-xs text-slate-500">{isPrivate ? 'Hidden from profile' : 'Visible on profile'}</p>
                    </div>
                  </div>
                  <div className={`w-10 h-6 rounded-full flex items-center transition-colors ${isPrivate ? 'bg-red-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${isPrivate ? 'translate-x-5' : 'translate-x-1'}`} />
                  </div>
                </button>

                {/* Notes Prompt */}
                <button
                  type="button"
                  onClick={() => { hapticMedium(); setShowNotesPrompt(!showNotesPrompt) }}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <FluentEmoji emoji="📝" size={18} />
                    <div className="text-left">
                      <p className="font-medium text-slate-900 dark:text-white text-sm">{showNotesPrompt ? 'Notes Prompt On' : 'Notes Prompt Off'}</p>
                      <p className="text-xs text-slate-500">{showNotesPrompt ? 'Ask for notes after completion' : "Don't show notes dialog"}</p>
                    </div>
                  </div>
                  <div className={`w-10 h-6 rounded-full flex items-center transition-colors ${showNotesPrompt ? 'bg-violet-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${showNotesPrompt ? 'translate-x-5' : 'translate-x-1'}`} />
                  </div>
                </button>
              </div>

              {/* Conquest */}
              {!habit.conquered_at && !isLinkedToChallenge && (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => { hapticMedium(); setIsCompletable(!isCompletable); if (!isCompletable) setTargetType('manual') }}
                    className="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <FluentEmoji emoji={isCompletable ? '🏆' : '🔥'} size={18} />
                      <div className="text-left">
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{isCompletable ? 'Conquest' : 'Ongoing'}</p>
                        <p className="text-xs text-slate-500">{isCompletable ? 'Can be completed permanently' : 'Track forever'}</p>
                      </div>
                    </div>
                    <div className={`w-10 h-6 rounded-full flex items-center transition-colors ${isCompletable ? 'bg-violet-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${isCompletable ? 'translate-x-5' : 'translate-x-1'}`} />
                    </div>
                  </button>

                  <AnimatePresence mode="wait">
                    {isCompletable && (
                      <motion.div
                        key="conquest-options"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-2 pl-3 border-l-2 border-violet-200 dark:border-violet-800"
                      >
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">When will you conquer it?</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { type: 'manual' as const, emoji: '🎯', label: "I'll decide" },
                            { type: 'streak' as const, emoji: '🔥', label: 'X days' },
                            { type: 'date' as const, emoji: '📅', label: 'By date' },
                          ].map((opt) => (
                            <button
                              key={opt.type}
                              type="button"
                              onClick={() => { hapticLight(); setTargetType(opt.type) }}
                              className={`p-2 rounded-xl border text-center transition-all ${
                                targetType === opt.type
                                  ? 'bg-violet-100 dark:bg-violet-900/30 border-violet-300 dark:border-violet-700'
                                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              <FluentEmoji emoji={opt.emoji} size={22} />
                              <span className={`block text-xs font-medium mt-0.5 ${targetType === opt.type ? 'text-violet-700 dark:text-violet-400' : 'text-slate-500'}`}>
                                {opt.label}
                              </span>
                            </button>
                          ))}
                        </div>

                        {targetType === 'streak' && (
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-slate-600 dark:text-slate-400">Days</span>
                              <span className="text-lg font-bold text-violet-600 dark:text-violet-400">{targetStreak}</span>
                            </div>
                            <input
                              type="range" min="7" max="365" value={targetStreak}
                              onChange={(e) => { hapticSelection(); setTargetStreak(parseInt(e.target.value)) }}
                              className="w-full accent-violet-500"
                            />
                            <div className="flex justify-between text-xs text-slate-400 mt-1"><span>1 week</span><span>1 year</span></div>
                          </motion.div>
                        )}

                        {targetType === 'date' && (
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800 space-y-2">
                            <div className="grid grid-cols-4 gap-1.5">
                              {[{ m: 1, l: '1M' }, { m: 3, l: '3M' }, { m: 6, l: '6M' }, { m: 12, l: '1Y' }].map((p) => (
                                <button key={p.l} type="button" onClick={() => setDatePreset(p.m)}
                                  className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-violet-300 dark:hover:border-violet-700 transition-colors text-xs font-medium text-slate-700 dark:text-slate-300"
                                >{p.l}</button>
                              ))}
                            </div>
                            <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
                              <PopoverTrigger asChild>
                                <button type="button" className="w-full p-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-400 hover:border-violet-400 transition-colors">
                                  <FluentEmoji emoji="🗓️" size={14} className="inline mr-1" /> Choose a date
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="center">
                                <Calendar mode="single" selected={targetDate ? new Date(targetDate) : undefined} onSelect={handleDateSelect} disabled={(d) => d < new Date()} />
                              </PopoverContent>
                            </Popover>
                            {targetDate && (
                              <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FluentEmoji emoji="📅" size={14} />
                                  <span className="text-sm font-medium text-violet-700 dark:text-violet-300">{format(new Date(targetDate), 'MMM d, yyyy')}</span>
                                </div>
                                <button type="button" onClick={() => setTargetDate('')} className="text-violet-500 text-sm">✕</button>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Already conquered message */}
              {habit.conquered_at && (
                <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900/50 flex items-center gap-3">
                  <FluentEmoji emoji="🏆" size={20} />
                  <div>
                    <p className="font-medium text-violet-800 dark:text-violet-200 text-sm">Already Conquered!</p>
                    <p className="text-xs text-violet-600 dark:text-violet-400">Conquered on {new Date(habit.conquered_at).toLocaleDateString()}</p>
                  </div>
                </div>
              )}

              {/* Core change warning */}
              {(isChangingCore || isChangingType) && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 flex items-start gap-2"
                >
                  <FluentEmoji emoji="💡" size={16} className="shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    You're changing core aspects. Your streak continues, but consider if this is truly the same habit.
                  </p>
                </motion.div>
              )}

              {/* Bottom spacer */}
              <div className="h-6" />
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
